"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, type RefObject } from "react";

/**
 * Returns a max-height (px) that keeps the referenced element within the
 * area visible above the on-screen keyboard.
 *
 * CSS viewport units (vh/svh/dvh) don't shrink when the iOS/Android software
 * keyboard opens, so a `max-h-[100dvh]` scroll container extends behind the
 * keyboard and its overflow becomes unreachable. The VisualViewport API does
 * track the keyboard, so we size the container against it instead.
 *
 * Returns `undefined` until measured (SSR / no VisualViewport support), letting
 * the caller fall back to a CSS class.
 */
export function useViewportSafeHeight(ref) {
  const $ = _c(3);
  const [maxHeight, setMaxHeight] = useState();
  let t0;
  let t1;
  if ($[0] !== ref) {
    t0 = () => {
      const viewport = window.visualViewport;
      if (!viewport) {
        return;
      }
      const update = function update() {
        const element = ref.current;
        if (!element || !window.visualViewport) {
          return;
        }
        const topWithinViewport = element.getBoundingClientRect().top - window.visualViewport.offsetTop;
        setMaxHeight(Math.max(0, window.visualViewport.height - topWithinViewport));
      };
      update();
      viewport.addEventListener("resize", update);
      viewport.addEventListener("scroll", update);
      return () => {
        viewport.removeEventListener("resize", update);
        viewport.removeEventListener("scroll", update);
      };
    };
    t1 = [ref];
    $[0] = ref;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return maxHeight;
}